import { Card, Chip, Divider, Typography } from "@mui/material";
import { usePageWrapper } from "../../../hooks";
import { ArtButton, DynamicForm, FormService, Identicon, useIdpService, validators } from '@art/shared-ui';
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, setUser } from "../../../store";
import { useIDPContext } from '../../../auth';
import { Box, Stack } from "@mui/material";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
const PersonalProfile = () => {
    const appState = useSelector((state: RootState) => state.auth);
    const idpService = useIdpService();
    const dispatch = useDispatch();
    const [formService, setFormService] = useState<FormService | null>(null);

    const { authGuardInstance } = useIDPContext() as {
        authGuardInstance: any;
    };
    const roles = Array.from(new Set(
        (appState.user?.tenant?.roles ?? authGuardInstance?.user?.tenant?.roles ?? [])
            .map((role: any) => typeof role === "string" ? role : role?.name || role?.title)
            .filter(Boolean),
    ));
    const tenant = appState.user?.tenant ?? authGuardInstance?.user?.tenant;
    const ownership = tenant?.is_owner;

    const contactSupport = () => {
        window.location.href = "mailto:info@aiotrix.com";
    }

    useEffect(() => {
        if (formService) {
            formService.setFormData(appState.user);
        }
    }, [formService, appState.user])

    const handleLogout = async () => {
        try {
            const logout: any = await authGuardInstance?.logout();
            if (logout) {
            } else {
            }
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    const formBuilder = (data: any) => {
        return [
            {
                type: 'row',
                children: [
                    {
                        type: 'col',
                        classNames: ['col-12', 'col-lg-6', 'responsive-input'],
                        children: [
                            {
                                id: "first_name",
                                label: "First Name",
                                type: "text",
                                placeholder: "Enter first name",
                                required: false,
                                value: data?.first_name,
                                validate: validators((value) => {
                                    if (!value || value.trim() === "") {
                                        return "First name is required.";
                                    }
                                    if (value.trim().length > 20) {
                                        return "First name must not exceed 20 characters.";
                                    }
                                    return null;
                                })
                            },
                        ]
                    }
                ]
            },
            {
                type: 'row',
                children: [
                    {
                        type: 'col',
                        classNames: ['col-12', 'col-lg-6', 'responsive-input'],
                        children: [
                            {
                                id: "last_name",
                                label: "Last Name",
                                type: "text",
                                placeholder: "Enter last name",
                                required: false,
                                marginBottom: 0,
                                value: data?.last_name,
                                validate: validators((value) => {
                                    if (!value || value.trim() === "") {
                                        return "Last name is required.";
                                    }
                                    if (value.trim().length > 20) {
                                        return "Last name must not exceed 20 characters.";
                                    }
                                    return null;
                                })
                            },
                        ]
                    }
                ]
            },
        ];
    };

    return (
        <>
            <Typography variant="h4">Account</Typography>
            <Typography variant="body2" className="mb-4">
                Update your personal profile and contact information.
            </Typography>

            <div className="row g-4">
                <div className="col-12 col-lg-4">
                    <div className="d-flex flex-column gap-3">
                        <Card className="text-center p-3">
                            <Identicon size={40} value={appState.user?.email} config={{ backColor: "#ede6e6", padding: 0.12 }} style={{ borderRadius: 20 }} />
                            <Typography variant="h6" overflow='auto'>
                                {appState.user?.first_name} {appState.user?.last_name}
                            </Typography>
                            <Typography variant="body2" >
                                {appState.user?.email}
                            </Typography>
                            <div className="mt-2 text-center">
                                <ArtButton label={"Logout"} variant="outlined" buttonType="button" onClick={handleLogout} />
                            </div>
                        </Card>

                         <Card className="p-4" data-qa="section-account-summary">
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Account summary
                            </Typography>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {appState.user?.first_name} {appState.user?.last_name}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Email
                                    </Typography>
                                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                                        {appState.user?.email}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Workspace
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                        <Typography variant="body1" fontWeight={600}>
                                            {tenant?.tenant_name || authGuardInstance?.tenantName || "—"}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={ownership === true ? "Owner" : ownership === false ? "Member" : "Ownership unverified"}
                                            color={ownership === true ? "primary" : "default"}
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Box>
                                <Divider />
                                <Box data-qa="profile-roles">
                                    <Typography variant="caption" color="text.secondary">
                                        Roles
                                    </Typography>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                                        {roles.length > 0
                                            ? roles.map((role) => <Chip key={role as string} size="small" label={role as string} />)
                                            : <Typography variant="body2" color="text.secondary">No role assigned</Typography>}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Card>

                        <Card className="text-center p-3" data-qa="section-contact-support">
                            <Typography variant="h5">Need Help?</Typography>
                            <Typography variant="body2" >
                                Our support team is here to assist you with any questions or
                                challenges
                            </Typography>
                            <div className="mt-2 text-center" data-qa="contact-support">
                                <ArtButton variant="outlined" label="Contact Support" buttonType="button" icon={faEnvelope} onClick={contactSupport} />
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="col-12 col-lg-8">
                    <Card className="p-3">

                        <div data-qa="section-personal-info">
                            <Typography variant="h5">Personal Info</Typography>
                            <Typography variant="body2" className="text-muted mb-3">
                                Here are your personal details, including name, contact
                                information, and other essential data. Ensure all information is
                                correct and up to date for a seamless experience across our
                                services. You can update your details anytime if needed.
                            </Typography>

                            <DynamicForm
                                formBuilder={formBuilder}
                                onInit={(service: FormService) => setFormService(service)}
                                onSubmit={idpService.saveData(`api/tenants/${authGuardInstance?.tenantName}/users/profile`, "put", {
                                    onApiCall: (data) => {
                                        return {
                                            ...data,
                                            email: appState.user?.email,
                                        };
                                    },
                                    onSuccess: ({ data }: any) => {
                                        dispatch(
                                            setUser({
                                                ...appState.user,
                                                first_name: data.first_name,
                                                last_name: data.last_name,
                                            })
                                        );
                                    },
                                    onError: (err: any) => {
                                        console.error(err);
                                    },
                                })}
                                autoSave={true}
                            />
                        </div>

                    </Card>
                </div>
            </div>
        </>
    );

};

export default usePageWrapper(PersonalProfile, {
    page_title: "Personal Profile"
});
